import { useWebSocket } from '@app:providers';
import { ProductionRequest, ProductionRequestStatusColorMap, ProductionRequestStatusEnum } from '@app:types';
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Spinner,
  Tag,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { Modal } from 'components/@common';
import { RiMore2Fill } from 'react-icons/ri';
import { formatDate, formatDaysAgo, prettifyObjectKey } from 'utils/helpers/formatting.helpers';
import { FormulationContainer } from './formulation-container';
import { AssignProductionToDeviceModal } from './production-request-assign-modal';

export function ProductionRequestCard(props: ProductionRequest & { isNext?: boolean }) {
  const websocket = useWebSocket();
  const isInProduction = props.status === ProductionRequestStatusEnum.IN_PRODUCTION;
  const { isOpen: isDetailsOpen, onClose: onDetailsClose, onOpen: onDetailsOpen } = useDisclosure();
  const { isOpen: isAssignmentOpen, onClose: onAssignmentClose, onOpen: onAssignmentOpen } = useDisclosure();

  return (
    <>
      <Card variant={'elevated'} minW='300px' height={'100%'} bgColor={isInProduction ? 'blue.50' : 'white'}>
        <CardHeader>
          <Flex alignItems='center' gap='10px'>
            <Flex flexDir={'column'} alignItems={'flex-start'} gap={2}>
              <Heading size='md'>
                {props.template.label} ({props.expected.weight}g)
              </Heading>
              <Badge variant='outline' colorScheme={ProductionRequestStatusColorMap[props.status]}>
                {props.status}
              </Badge>
            </Flex>
            <Spacer />
            {isInProduction ? (
              <Spinner />
            ) : (
              <ButtonGroup>
                <Menu>
                  <MenuButton size={'sm'} as={IconButton} icon={<RiMore2Fill />} variant='outline' aria-label='options' />
                  <MenuList>
                    {!props.isNext && (
                      <MenuItem onClick={() => websocket.send({ type: 'user.moveTaskToFront', payload: { requestId: props.id } as any })}>
                        Prioritise task
                      </MenuItem>
                    )}
                    <MenuItem onClick={onAssignmentOpen}>Assign to other device</MenuItem>
                    <MenuItem onClick={() => websocket.send({ type: 'user.deleteTask', payload: { requestId: props.id } as any })}>
                      Delete task
                    </MenuItem>
                  </MenuList>
                </Menu>
              </ButtonGroup>
            )}
          </Flex>
        </CardHeader>
        <CardBody py={0}>
          <ButtonGroup>
            <Button size={'sm'} variant={'outline'} onClick={onDetailsOpen}>
              View Details
            </Button>
          </ButtonGroup>
        </CardBody>
        <CardFooter>
          <Text fontSize={'sm'}>{isInProduction ? <i>Produced on the device.</i> : <i>{formatDaysAgo(props.createdAt)}</i>}</Text>
        </CardFooter>
      </Card>
      <Modal
        isOpen={isDetailsOpen}
        onClose={onDetailsClose}
        title='Product information'
        modalContentStyle={{ maxWidth: '90%', width: '800px' }}
      >
        <VStack spacing={4}>
          <Text fontSize='sm' px={4} w='100%'>
            Created at: {formatDate(props.createdAt, 'dateTime')}
          </Text>
          <Box w='100%' p={4}>
            <Heading pb={2} as={'h2'} size='sm' color={'black'} fontWeight={'bold'} mb={4}>
              General
            </Heading>
            <VStack spacing={3}>
              <Flex w='100%' gap={3} borderRadius={3} alignItems={'center'}>
                <Box as={'span'} flex={2}>
                  Product ID
                </Box>
                <Box as={'span'} flex={3}>
                  <Tag>{props.id}</Tag>
                </Box>
                <Box as={'span'} flex={2}>
                  <Badge variant='outline' colorScheme={ProductionRequestStatusColorMap[props.status]}>
                    {props.status}
                  </Badge>
                </Box>
              </Flex>
              <Flex w='100%' gap={3} borderRadius={3} alignItems={'center'}>
                <Box as={'span'} flex={2}>
                  Weight
                </Box>
                <Box as={'span'} flex={5}>
                  <Tag>{props.expected.weight}g</Tag>
                </Box>
              </Flex>
              {Object.entries(props.customizationAttributes || {}).map(([label, value]) => {
                return (
                  <Flex key='label' w='100%' gap={3} borderRadius={3} alignItems={'center'}>
                    <Box as={'span'} flex={2}>
                      {prettifyObjectKey(label)}
                    </Box>
                    <Box as={'span'} flex={5}>
                      <Tag>{value}</Tag>
                    </Box>
                  </Flex>
                );
              })}
            </VStack>
          </Box>
          <Box w='100%' p={4}>
            <Heading pb={2} as={'h2'} size='sm' color={'black'} fontWeight={'bold'} mb={4}>
              Expected formulation
            </Heading>
            <FormulationContainer formulation={props.expected.formulation} />
          </Box>
        </VStack>
      </Modal>
      <AssignProductionToDeviceModal
        isOpen={isAssignmentOpen}
        onClose={onAssignmentClose}
        productionRequestRef={{ requestId: props.id } as any}
        onSubmitSuccess={() => onAssignmentClose()}
      />
    </>
  );
}
